import React, {
    PropsWithChildren,
    SyntheticEvent,
    useCallback,
    useState,
} from "react";

import {
    ProductTileMapper,
    ProductTileModel,
} from "@api/mappers/products.mapper";

import { DeliveryTypes, StorePickerModalStates } from "@utils/enums";
import { isDefined } from "@utils/helpers";
import { useIsClientSide } from "@utils/hooks/isClient.hook";
import { getPreferredStoreCodeForStock } from "@utils/store";

import { useThunkDispatch } from "@redux/dispatcher";
import * as CartModule from "@redux/modules/api/data/cart/cart.module";
import { StoreDetailsModel } from "@redux/modules/api/data/stores/@types/storeDetails.model";
import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { HuboModal } from "@components/General/HuboModal/huboModal.component";
import { AddToCardModalFooter } from "@components/Modals/AddToCartModal/Partials/footer";
import { StorePickerModal } from "@components/Store/StorePickerModal/StorePickerModal.container";

import { ProductTileApiModel } from "lib/@types/service/product/api/productTile.api.model";

import { AddToCartButtonComponent } from "./addToCartButton.component";

type AddToCartButtonProps = PropsWithChildren<{
    addToCartItems: Array<{
        sku: string;
        amount: number;
        product?: ProductTileApiModel;
    }>;
}>;

const AddToCartButton = ({
    addToCartItems,
    children,
}: AddToCartButtonProps) => {
    const dispatch = useThunkDispatch();
    const { t } = useGranite();

    const isClient = useIsClientSide();

    const [storePickerModalState, setStorePickerModalState] = useState(
        StorePickerModalStates.Closed,
    );

    const [displayAddToCartModal, setDisplayAddToCartModal] = useState(false);

    const handleAction = useCallback(
        (storeCode: string | undefined) => {
            if (!isDefined(storeCode)) {
                return setStorePickerModalState(StorePickerModalStates.Open);
            }

            const { requestItems, trackingItems } = addToCartItems.reduce<{
                requestItems: {
                    sku: string;
                    quantity: number;
                    deliveryType: DeliveryTypes;
                }[];
                trackingItems: {
                    product?: ProductTileModel;
                    quantity: number;
                }[];
            }>(
                (items, { sku, amount, product }) => {
                    items.requestItems.push({
                        sku: sku,
                        quantity: amount,
                        deliveryType: DeliveryTypes.ClickAndCollect,
                    });
                    items.trackingItems.push({
                        product: product && ProductTileMapper(product),
                        quantity: amount,
                    });

                    return items;
                },
                { requestItems: [], trackingItems: [] },
            );

            dispatch(
                CartModule.addToCartBulk(
                    {
                        shopId: storeCode,
                        items: requestItems,
                    },
                    trackingItems,
                    true,
                ),
            ).then(action => {
                if (action.type === CartModule.APIStatus.SUCCESS) {
                    setDisplayAddToCartModal(true);
                }
            });
        },
        [setStorePickerModalState, dispatch],
    );

    const handleOnClick = useCallback(
        (event: SyntheticEvent<HTMLButtonElement>) => {
            event.preventDefault();
            handleAction(getPreferredStoreCodeForStock());
        },
        [handleAction],
    );

    const handleOnStorePickerModalClose = useCallback(
        () => setStorePickerModalState(StorePickerModalStates.Closed),
        [setStorePickerModalState],
    );

    const handleOnStorePickerModalSubmit = useCallback(
        async (store: StoreDetailsModel, onClose?: () => void) => {
            handleAction(store.code);
            onClose && onClose();
        },
        [],
    );

    if (!isClient) return null;

    return (
        <>
            <AddToCartButtonComponent onClick={handleOnClick}>
                {children}
            </AddToCartButtonComponent>
            <StorePickerModal
                displayStockLevel={false}
                isOnlyAvailableInStore={false}
                isOpen={storePickerModalState !== StorePickerModalStates.Closed}
                storeCode={getPreferredStoreCodeForStock()}
                onClose={handleOnStorePickerModalClose}
                buttonTextTK={"general.storepicker.button.confirmStoreAndOrder"}
                isLoading={false}
                filterOnClickAndCollect={addToCartItems.some(i =>
                    i.product?.deliveryTypes.some(
                        dt => dt === DeliveryTypes.ClickAndCollect,
                    ),
                )}
                onSubmit={handleOnStorePickerModalSubmit}
            />
            <HuboModal
                title={
                    addToCartItems.length > 1
                        ? t("cta.addtocart.multi.modal.title")
                        : t("cta.addtocart.modal.title")
                }
                customTitle={title => {
                    return (
                        <h2
                            className="text-secondary"
                            style={{ wordBreak: "break-word" }}
                        >
                            <i
                                className="las la-cart-arrow-down"
                                aria-hidden="true"
                            ></i>
                            {title}
                        </h2>
                    );
                }}
                modalState={displayAddToCartModal}
                toggleHandler={() => setDisplayAddToCartModal(false)}
                withFooter={false}
            >
                <AddToCardModalFooter
                    onModalClose={() => setDisplayAddToCartModal(false)}
                />
            </HuboModal>
        </>
    );
};

export { AddToCartButton };
